/** Distância entre o recorte e o balão: precisa caber a seta inteira. */
export const VAO = 52;
/** Respiro mínimo entre o balão e a borda da janela. */
export const BORDA = 12;
/** Tamanho da seta que aponta o alvo. */
export const TAMANHO_DA_SETA = 38;

const limitar = (valor, minimo, maximo) => Math.min(Math.max(valor, minimo), maximo);

const cabe = (lado, alvo, balao, janela) => {
  switch (lado) {
    case 'top':
      return alvo.top - VAO - balao.height >= BORDA;
    case 'bottom':
      return alvo.top + alvo.height + VAO + balao.height <= janela.height - BORDA;
    case 'left':
      return alvo.left - VAO - balao.width >= BORDA;
    case 'right':
      return alvo.left + alvo.width + VAO + balao.width <= janela.width - BORDA;
    default:
      return false;
  }
};

/** Ordem de tentativa quando o lado preferido não cabe. */
const ALTERNATIVAS = {
  bottom: ['bottom', 'top', 'right', 'left'],
  top: ['top', 'bottom', 'right', 'left'],
  left: ['left', 'right', 'bottom', 'top'],
  right: ['right', 'left', 'bottom', 'top'],
};

/**
 * Onde o balão fica, dado o alvo e o espaço da janela. O balão nunca cobre o
 * próprio alvo quando algum lado cabe, e nunca sai da janela: sem lado que
 * caiba, ele é trazido para dentro.
 */
export const posicionarBalao = (alvo, balao, janela, preferido = 'bottom') => {
  const lado =
    (ALTERNATIVAS[preferido] ?? ALTERNATIVAS.bottom).find((candidato) =>
      cabe(candidato, alvo, balao, janela)
    ) ?? preferido;

  const maxLeft = Math.max(BORDA, janela.width - balao.width - BORDA);
  const maxTop = Math.max(BORDA, janela.height - balao.height - BORDA);
  const leftCentralizado = alvo.left + alvo.width / 2 - balao.width / 2;
  const topCentralizado = alvo.top + alvo.height / 2 - balao.height / 2;

  switch (lado) {
    case 'top':
      return {
        lado,
        top: limitar(alvo.top - VAO - balao.height, BORDA, maxTop),
        left: limitar(leftCentralizado, BORDA, maxLeft),
      };
    case 'left':
      return {
        lado,
        top: limitar(topCentralizado, BORDA, maxTop),
        left: limitar(alvo.left - VAO - balao.width, BORDA, maxLeft),
      };
    case 'right':
      return {
        lado,
        top: limitar(topCentralizado, BORDA, maxTop),
        left: limitar(alvo.left + alvo.width + VAO, BORDA, maxLeft),
      };
    default:
      return {
        lado: 'bottom',
        top: limitar(alvo.top + alvo.height + VAO, BORDA, maxTop),
        left: limitar(leftCentralizado, BORDA, maxLeft),
      };
  }
};

/** Canto superior esquerdo da seta, encostada na borda do alvo voltada ao balão. */
export const posicionarSeta = (alvo, lado, tamanho = TAMANHO_DA_SETA) => {
  const meio = tamanho / 2;
  const centroX = alvo.left + alvo.width / 2 - meio;
  const centroY = alvo.top + alvo.height / 2 - meio;

  switch (lado) {
    case 'top':
      return { top: alvo.top - tamanho - 2, left: centroX };
    case 'left':
      return { top: centroY, left: alvo.left - tamanho - 2 };
    case 'right':
      return { top: centroY, left: alvo.left + alvo.width + 2 };
    default:
      return { top: alvo.top + alvo.height + 2, left: centroX };
  }
};

export const caixasSeTocam = (a, b) =>
  a.left < b.left + b.width &&
  b.left < a.left + a.width &&
  a.top < b.top + b.height &&
  b.top < a.top + a.height;
